/**
 * Sound Notification Utility
 * Plays notification sounds for admin alerts
 */

class NotificationSound {
  constructor() {
    this.audioContext = null;
    this.isEnabled = true;
    this.lastPlayTime = 0;
    this.minInterval = 1000; // Minimum 1 second between sounds
  }

  // Initialize audio context (must be called after user interaction)
  init() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    return this;
  }

  // Enable/disable sound notifications
  setEnabled(enabled) {
    this.isEnabled = enabled;
    localStorage.setItem('notificationSoundEnabled', enabled.toString());
  }

  // Check if sound is enabled
  getEnabled() {
    const stored = localStorage.getItem('notificationSoundEnabled');
    return stored === null ? true : stored === 'true';
  }

  // Play a standard alert notification sound
  playAlert() {
    if (!this.getEnabled()) return;
    
    // Prevent rapid fire sounds
    const now = Date.now();
    if (now - this.lastPlayTime < this.minInterval) return;
    this.lastPlayTime = now;

    try {
      this.init();
      
      // Create oscillator for a pleasant notification sound
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // Sound design: Two-tone pleasant chime
      oscillator.frequency.setValueAtTime(880, this.audioContext.currentTime); // A5
      oscillator.frequency.setValueAtTime(1320, this.audioContext.currentTime + 0.1); // E6
      oscillator.frequency.setValueAtTime(1760, this.audioContext.currentTime + 0.2); // A6
      
      oscillator.type = 'sine';
      
      // Volume envelope
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.3, this.audioContext.currentTime + 0.05);
      gainNode.gain.linearRampToValueAtTime(0.2, this.audioContext.currentTime + 0.15);
      gainNode.gain.linearRampToValueAtTime(0.15, this.audioContext.currentTime + 0.25);
      gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.4);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.4);
      
    } catch (error) {
      console.warn('Failed to play notification sound:', error);
    }
  }

  // Play a more urgent sound for new enquiries
  playEnquiryAlert() {
    if (!this.getEnabled()) return;
    
    // Prevent rapid fire sounds
    const now = Date.now();
    if (now - this.lastPlayTime < this.minInterval) return;
    this.lastPlayTime = now;

    try {
      this.init();
      
      // Create oscillator for enquiry notification
      const oscillator = this.audioContext.createOscillator();
      const oscillator2 = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      const gainNode2 = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      oscillator2.connect(gainNode2);
      gainNode.connect(this.audioContext.destination);
      gainNode2.connect(this.audioContext.destination);
      
      // First tone - higher pitch
      oscillator.frequency.setValueAtTime(1047, this.audioContext.currentTime); // C6
      oscillator.type = 'sine';
      
      // Second tone - harmonics
      oscillator2.frequency.setValueAtTime(1319, this.audioContext.currentTime); // E6
      oscillator2.type = 'sine';
      
      // Volume envelopes
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.25, this.audioContext.currentTime + 0.02);
      gainNode.gain.setValueAtTime(0.25, this.audioContext.currentTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.15);
      gainNode.gain.linearRampToValueAtTime(0.25, this.audioContext.currentTime + 0.2);
      gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.35);
      
      gainNode2.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode2.gain.linearRampToValueAtTime(0.15, this.audioContext.currentTime + 0.02);
      gainNode2.gain.setValueAtTime(0.15, this.audioContext.currentTime + 0.1);
      gainNode2.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.15);
      gainNode2.gain.linearRampToValueAtTime(0.15, this.audioContext.currentTime + 0.2);
      gainNode2.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.35);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.35);
      oscillator2.start(this.audioContext.currentTime);
      oscillator2.stop(this.audioContext.currentTime + 0.35);
      
    } catch (error) {
      console.warn('Failed to play enquiry notification sound:', error);
    }
  }

  // Simple beep for general notifications
  playBeep() {
    if (!this.getEnabled()) return;
    
    const now = Date.now();
    if (now - this.lastPlayTime < this.minInterval) return;
    this.lastPlayTime = now;

    try {
      this.init();
      
      const oscillator = this.audioContext.createOscillator();
      const gainNode = this.audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      oscillator.frequency.setValueAtTime(800, this.audioContext.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0, this.audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, this.audioContext.currentTime + 0.01);
      gainNode.gain.linearRampToValueAtTime(0, this.audioContext.currentTime + 0.15);
      
      oscillator.start(this.audioContext.currentTime);
      oscillator.stop(this.audioContext.currentTime + 0.15);
      
    } catch (error) {
      console.warn('Failed to play beep:', error);
    }
  }
}

// Create singleton instance
const notificationSound = new NotificationSound();

export default notificationSound;
